import React, { useState, useMemo, useRef } from 'react';
import styles from './index.module.scss';
import classnames from 'classnames';
import yaml from 'js-yaml';
import { Button } from 'tdesign-react';
import { ChevronDownIcon, ChevronUpIcon } from 'tdesign-icons-react';

interface Disk {
  disk: {
    name: string;
    size: string;
  };
}

interface Node {
  node: {
    name: string;
    cpu: string;
    memory: string;
    disks: Disk[];
  };
}

interface Subsystem {
  subsystem: {
    name: string;
    'server-number': string;
    nodes: Node[];
  };
}

interface Arch {
  arch: {
    name: string;
    subsystems: Subsystem[];
  };
}

interface Solution {
  solution: {
    name: string;
    description: string;
    arches: Arch[];
  };
}

interface ResourceData {
  description?: string;
  resources: Solution[];
}

interface ResourceRequirementProps {
  resourceRequirement: string;
}

const ResourceRequirement: React.FC<ResourceRequirementProps> = ({
  resourceRequirement
}) => {
  const [currentSolutionIndex, setCurrentSolutionIndex] = useState(0);
  const [expandedArches, setExpandedArches] = useState<{
    [key: string]: boolean;
  }>({});
  const archContentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // 解析YAML数据
  const resourceData = useMemo(() => {
    if (!resourceRequirement) return null;
    try {
      return yaml.load(resourceRequirement) as ResourceData;
    } catch (error) {
      console.error('Failed to parse resource requirement YAML:', error);
      return null;
    }
  }, [resourceRequirement]);

  if (
    !resourceData ||
    !resourceData.resources ||
    resourceData.resources.length === 0
  ) {
    return null;
  }

  const solutions = resourceData.resources;
  const currentSolution = solutions[currentSolutionIndex].solution;

  // 切换架构展开状态
  const toggleArchExpanded = (archIndex: number) => {
    const key = `${currentSolutionIndex}-${archIndex}`;
    setExpandedArches((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // 检查架构是否展开
  const isArchExpanded = (archIndex: number) => {
    const key = `${currentSolutionIndex}-${archIndex}`;
    return expandedArches[key] || false;
  };

  // 设置架构内容的引用
  const setArchContentRef = (archIndex: number, element: HTMLDivElement | null) => {
    const key = `${currentSolutionIndex}-${archIndex}`;
    archContentRefs.current[key] = element;
  };

  // 检查是否需要显示折叠按钮（当子系统数量>=2时显示）
  const shouldShowCollapseButton = (archItem: Arch) => {
    return archItem.arch.subsystems.length >= 2;
  };

  // 渲染磁盘信息
  const renderDisks = (disks: Disk[]): JSX.Element[] => {
    const diskRows: JSX.Element[] = [];
    for (let i = 0; i < disks.length; i += 3) {
      const rowDisks = disks.slice(i, i + 3);
      diskRows.push(
        <div key={i} className={styles.diskRow}>
          {rowDisks.map((diskItem, index) => (
            <div key={index} className={styles.diskItem}>
              <span className={styles.diskName}>{diskItem.disk.name}</span>
              <span className={styles.diskSize}>{diskItem.disk.size}</span>
            </div>
          ))}
        </div>
      );
    }
    return diskRows;
  };

  // 渲染节点信息
  const renderNodes = (nodes: Node[]) => {
    return nodes.map((nodeItem, nodeIndex) => (
      <div key={nodeIndex} className={styles.nodeContainer}>
        <div className={styles.nodeName}>{nodeItem.node.name}</div>
        <div className={styles.nodeSpecs}>
          <div className={styles.cpuMemoryRow}>
            <span className={styles.specItem}>
              <span className={styles.specLabel}>CPU</span>
              <span className={styles.specValue}>{nodeItem.node.cpu}</span>
            </span>
            <span className={styles.specItem}>
              <span className={styles.specLabel}>内存</span>
              <span className={styles.specValue}>{nodeItem.node.memory}</span>
            </span>
          </div>
          <div className={styles.disksContainer}>
            <div className={styles.disksTitle}>磁盘配置</div>
            {renderDisks(nodeItem.node.disks)}
          </div>
        </div>
      </div>
    ));
  };

  // 渲染子系统信息
  const renderSubsystems = (subsystems: Subsystem[]) => {
    return subsystems.map((subsystemItem, subsystemIndex) => (
      <div key={subsystemIndex} className={styles.subsystemContainer}>
        <div className={styles.subsystemHeader}>
          <span className={styles.subsystemName}>
            {subsystemItem.subsystem.name}
          </span>
          <span className={styles.serverNumber}>
            {subsystemItem.subsystem['server-number']}
          </span>
        </div>
        <div className={styles.nodesContainer}>
          {renderNodes(subsystemItem.subsystem.nodes)}
        </div>
      </div>
    ));
  };

  // 渲染架构信息
  const renderArch = (archItem: Arch, archIndex: number) => {
    const isExpanded = isArchExpanded(archIndex);
    const showCollapseButton = shouldShowCollapseButton(archItem);

    // 当有2个或以上子系统时，默认折叠显示第一个子系统
    const containerStyle =
      isExpanded || !showCollapseButton
        ? {}
        : { maxHeight: '500px', overflow: 'hidden' };
    return (
      <div key={archIndex} className={styles.archContainer}>
        <div className={styles.archHeader}>
          <h4 className={styles.archName}>{archItem.arch.name}</h4>
        </div>
        <div
          ref={(el) => setArchContentRef(archIndex, el)}
          className={styles.archContent}
          style={containerStyle}
        >
          {renderSubsystems(archItem.arch.subsystems)}
        </div>
        {showCollapseButton && (
          <div className={styles.expandButtonContainer}>
            <Button
              variant="text"
              size="small"
              onClick={() => toggleArchExpanded(archIndex)}
              icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            >
              {isExpanded ? '收起' : '展开'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.resourceRequirementContainer}>
      {/* 解决方案Tab切换器 */}
      {solutions.length > 1 && (
        <div className={styles.solutionSwitcher}>
          {solutions.map((solutionItem, index) => (
            <div
              key={index}
              className={classnames([
                styles.solutionTab,
                currentSolutionIndex === index
                  ? styles.solutionTabActive
                  : styles.solutionTabInactive
              ])}
              onClick={() => setCurrentSolutionIndex(index)}
            >
              {solutionItem.solution.name}
            </div>
          ))}
        </div>
      )}

      {/* 当前解决方案内容 */}
      <div className={styles.solutionContent}>
        {/* 解决方案描述 */}
        <div className={styles.solutionDescription}>
          {currentSolution.description}
        </div>

        {/* 架构列表 */}
        <div className={styles.archesContainer}>
          {currentSolution.arches.map((arch, archIndex) =>
            renderArch(arch, archIndex)
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceRequirement;
